import { ElementRef, useEffect, useRef, useState } from 'react';

import { ProductCard } from './ProductCard';

import { AnimateSpin } from '@/components/AnimateSpin';
import { SearchProductsFilter } from '@/components/SearchProductsFilter';
import { SearchProductsForm } from '@/components/SearchProductsForm';
import { withSearchProductsFilters } from '@/hocs/withSearchProductsFilter';
import { useGetProducts } from '@/hooks/useGetProducts';

interface ProductSelectProps {
  isSelected: boolean;
}

export const ProductSelect = withSearchProductsFilters<ProductSelectProps>(
  ({ isSelected, searchFilters, handleSearchFilters, handleResetFilters }) => {
    const [isSaving, setIsSaving] = useState(false);

    const endProductsRef = useRef<ElementRef<'li'>>(null);

    const {
      products,
      fetchNextPage,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
    } = useGetProducts({ ...searchFilters, unique: true });

    useEffect(() => {
      if (!endProductsRef.current) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          const { isIntersecting } = entries[0];

          if (!hasNextPage) {
            obs.disconnect();
            return;
          }

          if (isIntersecting && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          rootMargin: '30px',
        },
      );

      observer.observe(endProductsRef.current);

      return () => {
        observer.disconnect();
      };
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    useEffect(() => {
      if (!isSelected) {
        handleResetFilters();
      }
    }, [isSelected, handleResetFilters]);

    return (
      <div className="space-y-8">
        <div className="relative flex-1">
          <SearchProductsForm
            onSearchFilters={handleSearchFilters}
            searchFilters={searchFilters}
          />

          <SearchProductsFilter
            onSearchFilters={handleSearchFilters}
            searchFilters={searchFilters}
          />
        </div>

        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {products.map((product) => (
            <li key={product.id}>
              <ProductCard
                product={product}
                isSaving={isSaving}
                onSaving={setIsSaving}
              />
            </li>
          ))}

          <li ref={endProductsRef}>{isFetching && <AnimateSpin />}</li>
        </ul>
      </div>
    );
  },
);
